/*    
<summary>
  This functional component "AddShiftManagementForm" provides form to add new user or update existing user.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<returns>Returns JSX</returns>
*/

import { Field, Form, Formik } from "formik";
import { observer } from "mobx-react";
import moment from "moment";
import { Button, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { colorRegex } from "../../../constants/constants";
import { IOption } from "../../../models/ICommon";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import FormikFormInput from "../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormTextArea from "../../../shared-components/FormikFormHandlers/FormikFormTextArea";
import FormikFormTimePicker from "../../../shared-components/FormikFormHandlers/FormikFormTimePicker";
import {
  AddShiftValidationSchema,
  EditShiftValidationSchema,
} from "../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import LoaderButton from "../../../shared-components/button/loader-button";
import Label from "../../../shared-components/label/label";
import { formatMessage } from "../../../translations/format-message";

interface IProps {
  id: number;
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  getShiftManagementLoading: boolean;
  addUpdateShiftManagementLoading: boolean;
  initialValues: any;
  businessTypeOptions: IOption[];
  setShowDeletePopup: (show: boolean) => void;
  showDeletePopup: boolean;
}

const AddShiftManagementForm = (props: IProps) => {
  const handleDelete = () => {
    props.setShowDeletePopup(true);
  };

  return (
    <>
      <Modal show={true} centered>
        <Modal.Header>
          <Modal.Title>
            {props.id > 0 ? (
              <FormattedMessage id="shift_details" />
            ) : (
              <FormattedMessage id="shift_registration" />
            )}
            <button
              onClick={props.onModalClosed}
              disabled={props.addUpdateShiftManagementLoading}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{
            ...props.initialValues,
            SelectedColorCode: props.initialValues.Colour,
          }}
          enableReinitialize
          validationSchema={
            props.id === -1
              ? AddShiftValidationSchema
              : EditShiftValidationSchema
          }
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={async (values) => {
            const data = {
              ...values,
              StartTime:
                props.id > 0 && typeof values.StartTime === "string"
                  ? values.StartTime
                  : moment(values.StartTime, "HH:mm").format("HH:mm:ss"),
              EndTime:
                props.id > 0 && typeof values.EndTime === "string"
                  ? values.EndTime
                  : moment(values.EndTime, "HH:mm").format("HH:mm:ss"),
              IsTimeChanged:
                props.id > 0 &&
                typeof values.StartTime === "string" &&
                typeof values.EndTime === "string" &&
                (values.StartTime !== props.initialValues?.StartTime ||
                  values.EndTime !== props.initialValues?.EndTime)
                  ? true
                  : false,
            };
            props.submitHandler(data);
          }}
        >
          {({ setFieldValue, values, errors, touched }) => (
            <Form className="user-form">
              {props.getShiftManagementLoading && (
                <FormLoader loading={props.getShiftManagementLoading} />
              )}
              <Modal.Body>
                {/* Row 1: Shift Name and Shift Short Name */}
                <div className="form-group mb-3 row">
                  <div className="col-sm-6">
                    <Label
                      className="label-style"
                      required={true}
                      label="shift_name"
                    />
                    <Field
                      className="form-control"
                      name="ShiftName"
                      type="text"
                      placeholder="enter_shift_name"
                      as={FormikFormInput}
                    />
                  </div>
                  <div className="col-sm-6">
                    <Label
                      className="label-style"
                      required={true}
                      label="shift_short_name"
                    />
                    <Field
                      className="form-control"
                      name="ShiftShortName"
                      type="text"
                      placeholder="enter_shift_short_name"
                      as={FormikFormInput}
                    />
                  </div>
                </div>

                {/* Row 2: Start Time, End Time, and Color Selector */}
                <div className="form-group mb-3 row">
                  <div className="col-sm-4">
                    <Label
                      className="label-style"
                      required={true}
                      label="start_time"
                    />
                    <Field
                      className="form-control"
                      name="StartTime"
                      as={FormikFormTimePicker}
                      // onChange={(e: any) => {
                      //   setFieldValue("StartTime", e.target.value);
                      // }}
                    />
                  </div>
                  <div className="col-sm-4">
                    <Label
                      className="label-style"
                      required={true}
                      label="end_time"
                    />
                    <Field
                      className="form-control"
                      name="EndTime"
                      as={FormikFormTimePicker}
                    />
                  </div>

                  <div className="col-sm-4">
                    <Label
                      className="label-style"
                      required={true}
                      label="color"
                    />
                    <div className="d-flex align-items-center">
                      <Field
                        className="form-control"
                        name="Colour"
                        type="color"
                        onChange={(e: any) => {
                          const selectedColor = e.target.value;
                          setFieldValue("Colour", selectedColor);
                          setFieldValue("SelectedColorCode", selectedColor);
                        }}
                      />
                      <div className="selected-color-preview ms-2">
                        <Field
                          className="form-control"
                          name="SelectedColorCode"
                          type="text"
                          disabled
                        />
                      </div>
                    </div>
                    {touched.Colour &&
                      !colorRegex.test(values.Colour) &&
                      errors.Colour && (
                        <div className="errorMessage text-danger">
                          {formatMessage(errors.Colour as string)}
                        </div>
                      )}
                  </div>
                </div>

                {/* Row 3: Memo Field */}
                <div className="form-group mb-3 row">
                  <div className="col-12">
                    <Label
                      className="label-style"
                      required={false}
                      label="memo"
                    />
                    <Field
                      className="form-control"
                      name="Memo"
                      as={FormikFormTextArea}
                      rows="4"
                      placeholder="enter_additional_information_here"
                    />
                    <p className="small text-muted text-end mt-1">{`${values.Memo.length}/300`}</p>
                  </div>
                </div>
                <div className="col-sm-4 mb-1">
                  <Label className="label-style" label="status" />
                  <div className="row">
                    <div className="col-sm-6">
                      <Field
                        className="form-check-input"
                        name="ShiftStatus"
                        type="radio"
                        value="0"
                        id="valid"
                        // disabled={viewOnly}
                      />
                      <label className="form-check-label" htmlFor="valid">
                        <FormattedMessage id="valid" />
                      </label>
                    </div>
                    <div className="col-sm-6">
                      <Field
                        className="form-check-input"
                        name="ShiftStatus"
                        type="radio"
                        value="1"
                        id="invalid"
                        // disabled={viewOnly}
                      />
                      <label className="form-check-label" htmlFor="invalid">
                        <FormattedMessage id="invalid" />
                      </label>
                    </div>
                  </div>
                </div>
              </Modal.Body>

              {/* Footer Buttons */}
              <div className="modal-footer center-content">
                <Button
                  variant="outline-danger"
                  type="button"
                  onClick={props.onModalClosed}
                  disabled={props.addUpdateShiftManagementLoading}
                >
                  <i className="bi bi-x-lg me-1"></i>
                  <FormattedMessage id="close" />
                </Button>
                <LoaderButton
                  type="submit"
                  iconName="bi-check-lg"
                  isLoading={props.addUpdateShiftManagementLoading}
                  text={props.id > -1 ? "button_update" : "button_register"}
                />
                {props.id > -1 && (
                  <Button
                    variant="danger"
                    type="button"
                    onClick={handleDelete}
                    disabled={props.addUpdateShiftManagementLoading}
                  >
                    <i className="bi bi-trash3-fill me-1"></i>
                    <FormattedMessage id="button_delete" />
                  </Button>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

export default observer(AddShiftManagementForm);
